import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, PackageX, ShoppingCart } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { API, formatPrice, productImages, qtyDiscountFactor, waLink } from "../../api";
import { useCart } from "../../CartContext";
import Logo, { WhatsAppIcon } from "../Logo";

const QTY_OPTIONS = [
  { value: 1, label: "1 ud." },
  { value: 5, label: "5 uds." },
  { value: 10, label: "10 uds." },
  { value: 50, label: "50 uds." },
];

export default function ProductCard({ product }) {
  const [flavor, setFlavor] = useState(product.flavors?.[0] || "");
  const [qty, setQty] = useState(1);
  const [qtyInput, setQtyInput] = useState("1");
  const [imgIdx, setImgIdx] = useState(0);
  const { addItem } = useCart();
  const images = productImages(product);

  const applyQty = (n) => {
    const v = Math.max(1, Math.min(999, n));
    setQty(v);
    setQtyInput(String(v));
  };

  const basePrice = product.old_price ?? product.price;
  const bulkFactor = qtyDiscountFactor(qty);
  const bulkPct = Math.round((1 - bulkFactor) * 100);
  const unitPrice = basePrice * bulkFactor;
  const total = qty * basePrice * bulkFactor;
  const outOfStock = (product.stock ?? 0) <= 0;

  const message = useMemo(() => {
    const lines = [
      "Hola NubeVape España, quiero hacer un pedido:",
      "",
      `• Producto: ${product.name}`,
      flavor ? `• Sabor/Combo: ${flavor}` : null,
      qty >= 10
        ? `• Cantidad: ${qty} unidades (puedo mezclar sabores sin coste extra)`
        : `• Cantidad: ${qty} ${qty === 1 ? "unidad" : "unidades"}`,
      bulkPct > 0 ? `• Descuento por cantidad: -${bulkPct}%` : null,
      `• Total estimado: ${formatPrice(total)}`,
      "",
      "¿Me confirmáis disponibilidad y entrega? ¡Gracias!",
    ].filter(Boolean);
    return lines.join("\n");
  }, [product, flavor, qty, total, bulkPct]);

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="flex flex-col overflow-hidden rounded-2xl border border-[#2D323E] card-glow transition-all duration-300 hover:-translate-y-1.5 hover:border-[#2EE6D6]/50 hover:shadow-[0_10px_30px_-10px_rgba(46,230,214,0.2)]"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#16181D]">
        {images.length > 0 ? (
          <img
            src={`${API}/images/${images[Math.min(imgIdx, images.length - 1)]}`}
            alt={product.name}
            data-testid={`product-image-${product.id}`}
            className="h-full w-full object-contain p-3"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center opacity-30">
            <Logo />
          </div>
        )}
        {images.length > 1 && (
          <>
            <button
              type="button"
              data-testid={`product-image-prev-${product.id}`}
              onClick={() => setImgIdx((imgIdx - 1 + images.length) % images.length)}
              aria-label="Imagen anterior"
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-[#16181D]/70 p-1.5 text-[#F2F4F6] backdrop-blur transition-colors hover:bg-[#2EE6D6] hover:text-black"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              data-testid={`product-image-next-${product.id}`}
              onClick={() => setImgIdx((imgIdx + 1) % images.length)}
              aria-label="Imagen siguiente"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-[#16181D]/70 p-1.5 text-[#F2F4F6] backdrop-blur transition-colors hover:bg-[#2EE6D6] hover:text-black"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`product-image-dot-${product.id}-${i}`}
                  onClick={() => setImgIdx(i)}
                  aria-label={`Ver imagen ${i + 1}`}
                  className={`h-1.5 w-1.5 rounded-full transition-colors ${
                    i === imgIdx ? "bg-[#2EE6D6]" : "bg-[#F2F4F6]/30"
                  }`}
                />
              ))}
            </div>
          </>
        )}
        {bulkPct > 0 && (
          <span
            data-testid={`product-discount-badge-${product.id}`}
            className="absolute left-3 top-3 rounded-full bg-gradient-to-r from-[#2EE6D6] to-[#7C5CFF] px-3 py-1 text-xs font-bold text-black"
          >
            -{bulkPct}% DTO
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-4 p-5">
        <div>
          <h3 className="font-heading text-xl font-semibold">{product.name}</h3>
          <p className="mt-1 text-sm text-[#8A8F98]">{product.tagline}</p>
        </div>

        {product.specs?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {product.specs.map((s) => (
              <span
                key={s}
                className="rounded-full border border-[#2D323E] bg-[#16181D] px-2.5 py-0.5 text-[11px] font-medium text-[#8A8F98]"
              >
                {s}
              </span>
            ))}
          </div>
        )}

        {product.flavors?.length > 0 && (
          <div>
            <label
              htmlFor={`flavor-${product.id}`}
              className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-[#8A8F98]"
            >
              Sabor / Combo ({product.flavors.length})
            </label>
            <Select value={flavor} onValueChange={setFlavor}>
              <SelectTrigger
                id={`flavor-${product.id}`}
                data-testid={`product-flavor-select-${product.id}`}
                className="w-full border-[#2D323E] bg-[#16181D] text-[#F2F4F6]"
              >
                <SelectValue placeholder="Elige sabor" />
              </SelectTrigger>
              <SelectContent className="border-[#2D323E] bg-[#1E2128] text-[#F2F4F6]">
                {product.flavors.map((f) => (
                  <SelectItem key={f} value={f} data-testid={`product-flavor-option-${product.id}-${f}`}>
                    {f}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-[#8A8F98]">
            Cantidad
          </span>
          <div className="grid grid-cols-4 gap-2">
            {QTY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                data-testid={`product-qty-btn-${product.id}-${opt.value}`}
                onClick={() => applyQty(opt.value)}
                className={`rounded-lg border py-2 text-sm font-semibold transition-colors ${
                  qty === opt.value
                    ? "border-[#2EE6D6] bg-[#2EE6D6]/10 text-[#2EE6D6]"
                    : "border-[#2D323E] text-[#8A8F98] hover:border-[#7C5CFF]/50 hover:text-[#F2F4F6]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <label
              htmlFor={`qty-custom-${product.id}`}
              className="text-xs text-[#8A8F98]"
            >
              Otra cantidad:
            </label>
            <input
              id={`qty-custom-${product.id}`}
              data-testid={`product-qty-custom-${product.id}`}
              type="number"
              min="1"
              max="999"
              value={qtyInput}
              onChange={(e) => {
                const v = e.target.value;
                setQtyInput(v);
                const n = parseInt(v, 10);
                if (!Number.isNaN(n) && n >= 1) applyQty(Math.min(999, n));
              }}
              onBlur={() => setQtyInput(String(qty))}
              className="w-20 rounded-lg border border-[#2D323E] bg-[#16181D] px-2 py-1.5 text-sm text-[#F2F4F6] focus:border-[#2EE6D6] focus:outline-none"
            />
          </div>
          <p
            data-testid={`product-bulk-hint-${product.id}`}
            className="mt-2 text-xs text-[#8A8F98]"
          >
            Descuentos por cantidad:{" "}
            <span className="font-semibold text-[#2EE6D6]">10+ uds = -25%</span>
            {" · "}
            <span className="font-semibold text-[#7C5CFF]">50+ uds = -50%</span>
          </p>
          {qty >= 10 && (
            <p className="mt-1.5 text-xs text-[#2EE6D6]">
              Puedes mezclar sabores sin coste extra.
            </p>
          )}
        </div>

        <div className="mt-auto flex items-end justify-between">
          <div>
            <div className="flex flex-wrap items-baseline gap-2">
              <span
                data-testid={`product-price-${product.id}`}
                className="font-heading text-2xl font-extrabold text-gradient"
              >
                {formatPrice(unitPrice)}
              </span>
              {bulkPct > 0 && (
                <span className="text-sm text-[#8A8F98] line-through">
                  {formatPrice(basePrice)}
                </span>
              )}
              {bulkPct > 0 && (
                <span
                  data-testid={`product-bulk-badge-${product.id}`}
                  className="rounded-full bg-[#7C5CFF]/15 px-2 py-0.5 text-[11px] font-bold text-[#7C5CFF]"
                >
                  -{bulkPct}% por cantidad
                </span>
              )}
            </div>
            {qty > 1 && (
              <p data-testid={`product-total-${product.id}`} className="text-xs text-[#8A8F98]">
                Total {qty} uds:{" "}
                <span className="font-semibold text-[#F2F4F6]">{formatPrice(total)}</span>
              </p>
            )}
          </div>
          {outOfStock && (
            <span
              data-testid={`product-stock-${product.id}`}
              className="text-xs font-medium text-[#EF4444]"
            >
              Agotado
            </span>
          )}
        </div>

        {outOfStock ? (
          <span className="flex items-center justify-center gap-2 rounded-full border border-[#2D323E] py-3 text-sm font-semibold text-[#8A8F98]">
            <PackageX className="h-4 w-4" /> Sin stock disponible
          </span>
        ) : (
          <div className="flex flex-col gap-2">
            <a
              href={waLink(message)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`whatsapp-order-btn-${product.id}`}
              className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] py-3 text-sm font-bold text-black shadow-[0_4px_14px_rgba(37,211,102,0.3)] transition-all hover:bg-[#20BA5A] hover:shadow-[0_6px_20px_rgba(37,211,102,0.45)]"
            >
              <WhatsAppIcon className="h-4 w-4" />
              Pedir por WhatsApp
            </a>
            <button
              type="button"
              onClick={() => addItem(product, flavor, qty)}
              data-testid={`add-to-cart-btn-${product.id}`}
              className="flex items-center justify-center gap-2 rounded-full border border-[#7C5CFF]/60 py-3 text-sm font-bold text-[#C9BEFF] transition-colors hover:bg-[#7C5CFF] hover:text-white"
            >
              <ShoppingCart className="h-4 w-4" />
              Añadir al carrito
            </button>
          </div>
        )}
      </div>
    </article>
  );
}
